/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject, Injectable } from '@angular/core';

import { of } from 'rxjs';
import { STD_ANIMATION } from '@src/app/shared/std-animation';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { required } from '@src/app/shared/validators';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { VatReturnPageNavRoute } from '@src/app/routes/app-router';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MessageService } from '@src/app/shared/message.service';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { map } from 'rxjs/operators';
import { ErrorCode } from '@src/app/shared/form/form-error/form-error.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { GridField } from '@src/app/shared/grid/grid-field';
import { Field } from '@src/app/shared/field/Field';
import { Txn } from '@src/app/model/txn';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';

export class VatReturnTxn extends AbstractObject {
    ledger: 'ar'|'ap';
    netAmount: number;
    vatAmount: number;
    // Joins
    txnId: uuid;
    receiptId: uuid;
    refNr: number;
    receiptNr: number;
    txnDate: string;
    typeId: uuid;
}
export class VatReturn extends AbstractObject {
    fromDate: string;
    endDate: string;
    notes: string;
    basis: uuid;

    arTransactions: VatReturnTxn[] = [];
    apTransactions: VatReturnTxn[] = [];
    // Calculated client side
    outputVatAmount = 0;
    outputNetAmount = 0;
    outputItems = 0;
    outputTotal = 0;

    inputVatAmount = 0;
    inputNetAmount = 0;
    inputItems = 0;
    inputTotal = 0;

    toBePaid = 0;
    toBeReclaimed = 0;

    static bases = [
        {id: 'cash', name: 'Cash Receipts Basis'},
        {id: 'invoice', name: 'Invoice Basis'}
    ]
}

@Injectable({
    providedIn: 'root'
})
export class VatReturnService extends AbstractHttpService {
    http = inject(HttpClient);
    messageService = inject(MessageService);

    protected get baseUrl(): string {
        return this.getAjaxPath('vatReturn');
    }

    protected cache: VatReturn[];
    protected typeString = 'VAT Return';
}

@Component({
    selector: 'app-vat-return-page',
    templateUrl: './vat-return-page.component.html',
    styleUrls: ['./vat-return-page.component.scss'],
    animations: [STD_ANIMATION],
    imports: [FormPageComponent]
})
export class VatReturnPageComponent extends AbstractPageComponent {
    dataSvc = inject(VatReturnService);
    cds = inject(ConfirmDialogService);

    endDateField = FormDateComponent.make('Return End Date', 'endDate', {validators: [required]});
    notesField = FieldMaker.notes();
    defaultBasis: AbstractObject;

    basisField = FormPicklistComponent.make('VAT Basis', 'basis', null, {items: VatReturn.bases}, {asyncValidators: [this.validateBasis.bind(this)]});

    arTxnField = new GridField({
        field:
            { label: $localize`Output Transactions (VAT Charged Out)`, value: 'arTransactions', visible: Field.formOnly, formRow: 2, sendServer: false  },
        rowFactory: this.txnRowFactory.bind(this),
    });
    apTxnField = new GridField({
        field:
            { label: $localize`Input Transactions (VAT On Purchases)`, value: 'apTransactions', visible: Field.formOnly, formRow: 2, formColumn: 2, sendServer: false },
        rowFactory: this.txnRowFactory.bind(this),
    });    

    config = new FormConfig({
        navRoute: VatReturnPageNavRoute,
        title: $localize`VAT Return`,
        help: $localize`VAT Return Reports`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                this.endDateField,
                this.basisField,
                this.notesField,
                FormNumberComponent.makeCurrency('Output Nett Amount', 'outputNetAmount', {formColumn: 2, readonly: true, visible: Field.formOnly, sendServer: false }),
                FormNumberComponent.makeCurrency('Output VAT Amount (VAT Charged Out)', 'outputVatAmount', {formColumn: 2, readonly: true, visible: Field.formOnly, sendServer: false }),
                FormNumberComponent.makeCurrency('Output Total', 'outputTotal', {formColumn: 2, readonly: true, visible: Field.formOnly, sendServer: false }),
                FormNumberComponent.make('Output Transaction Count', 'outputItems', {}, {formColumn: 2, readonly: true, visible: Field.formOnly, sendServer: false }),
                FormNumberComponent.makeCurrency('Input Nett Amount', 'inputNetAmount', {formColumn: 3, readonly: true, visible: Field.formOnly, sendServer: false }),
                FormNumberComponent.makeCurrency('Input VAT Amount (VAT On Purchases)', 'inputVatAmount', {formColumn: 3, readonly: true, visible: Field.formOnly, sendServer: false }),
                FormNumberComponent.makeCurrency('Input Total', 'inputTotal', {formColumn: 3, readonly: true, visible: Field.formOnly, sendServer: false }),
                FormNumberComponent.make('Input Transaction Count', 'inputItems', {}, {formColumn: 3, readonly: true, visible: Field.formOnly, sendServer: false }),
                FormTextComponent.make('Transaction Date Range', 'txnPeriods', {
                    calculateValue: (o:VatReturn) => o.fromDate + " -> " + o.endDate,
                    formColumn: 4, readonly: true, sendServer: false
                }),
                FormNumberComponent.makeCurrency('VAT To Be Paid', 'toBePaid', {formColumn: 4, readonly: true, visible: Field.formOnly, sendServer: false }),
                FormNumberComponent.makeCurrency('VAT To Be Reclaimed', 'toBeReclaimed', {formColumn: 4, readonly: true, visible: Field.formOnly, sendServer: false }),                
                this.apTxnField,
                this.arTxnField,
            ],
            formLayout: [{ cells: [{ width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }] },
    { cells: [{ colspan: 2, pageTab: 'yes'}, {colspan: 2, pageTab: 'yes'}] }],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.objectFactory.bind(this),
        beforeEdit: this.beforeEdit.bind(this)
    });

    validateBasis(p1: AppFormControl) {
        const chgMsg = `Your default basis was ${this.defaultBasis?.name} are you sure you have filed appropriate paper work to change basis`;
        const firstMsg = `This is your first return - Make sure you are choosing correct registerered basis. ALL EXISTING TRANSACTIONs up to the date specified are included in first return. This includes any data that you have imported. You may want to create a dummy return first to mark imported data as returned, before creating your first real return`
        const msg = this.defaultBasis ? chgMsg : firstMsg;
        if (p1.value !== this.defaultBasis?.id && p1.dirty) {
            return this.cds.openConfirm($localize `Confirm Basis`, msg,).pipe (map( r => {
                if (r === 'OK') {
                    console.log('Continue');
                    return null;
                }
                console.log('e');
                return new ErrorCode('InvalidBasis', 'Correct return basis').new();
            }));
        }
        return of(null);
    }

    objectFactory() {
        this.endDateField.enableControl();
        this.dataSvc.get<VatReturn>(false, new HttpParams().set('_sortDesc', 'endDate')).subscribe( vrts => {
            if (vrts && vrts.length > 0) {
                this.basisField.control.setValue(vrts[0].basis, {emitEvent: false});
                this.basisField.disableControl();
            } else {
                this.basisField.enableControl();
            }
        })
        return of(new VatReturn);
    }

    beforeEdit(o: VatReturn) {
        o.outputVatAmount = 0;
        o.outputNetAmount = 0;
        o.outputItems = 0;
        o.outputTotal = 0;

        this.endDateField.disableControl();
        this.basisField.disableControl();

        o.arTransactions.forEach ( vrt => {
            o.outputVatAmount += vrt.vatAmount;
            o.outputNetAmount += vrt.netAmount;
            o.outputItems += 1;
            o.outputTotal += vrt.vatAmount;
            o.outputTotal += vrt.netAmount;
        })

        o.inputVatAmount = 0;
        o.inputNetAmount = 0;
        o.inputItems = 0;
        o.inputTotal = 0;

        o.apTransactions.forEach ( vrt => {
            o.inputVatAmount += vrt.vatAmount;
            o.inputNetAmount += vrt.netAmount;
            o.inputItems += 1;
            o.inputTotal += vrt.netAmount;
            o.inputTotal += vrt.vatAmount;
        })

        const toPay = o.outputVatAmount - o.inputVatAmount
        o.toBePaid = Math.max(toPay, 0);
        o.toBeReclaimed = 0 - Math.min(toPay, 0);
        
        return o;
    }

    txnRowFactory() {
        const txnLink = FormNavLinkComponent.makeTxnLink('RefNr');
        txnLink.calculateValue = (o: VatReturnTxn) => o.refNr;
        txnLink.btnOpts.navRouteCalculator = (o: VatReturnTxn) => {
            if (o) {
                const txnRoute = FormNavLinkComponent.getTxnRoute({typeId: o.typeId} as Txn);
                if (txnRoute?.route) {
                    return txnRoute.route.getIdUrl(o.txnId);
                } else {
                    console.log('Do not know how to navigate to ', {o: txnRoute})
                }
            }
            return null; // Do not navigate if no id value on item provided            
        }
        const receiptLink = FormNavLinkComponent.makeTxnLink('Payment Ref');
        receiptLink.calculateValue = (o: VatReturnTxn) => o.receiptNr;
        receiptLink.name = 'receiptLink';
        receiptLink.btnOpts.navRouteCalculator = (o: VatReturnTxn) => {
            if (o) {
                const typeIdTxn = o.ledger === 'ar' ? {typeId: Txn.TYPE.BANK_IN.id} : {typeId: Txn.TYPE.BANK_OUT.id}
                const txnRoute = FormNavLinkComponent.getTxnRoute(typeIdTxn as Txn);
                if (txnRoute?.route) {
                    return txnRoute.route.getIdUrl(o.receiptId);
                } else {
                    console.log('Do not know how to navigate to ', {o: txnRoute})
                }
            }
            return null; // Do not navigate if no id value on item provided            
        }        

        return [
            FormPicklistComponent.make('Type', 'typeId', 'type', { items: Txn.TYPES }, { readonly: true }),
            txnLink,
            FormDateComponent.make('Transaction Date', 'txnDate', {readonly: true}),
            receiptLink,
            FormDateComponent.make('Payment Date', 'paymentDate', {readonly: true}),
            FormNumberComponent.makeCurrency('Nett Amount', 'netAmount',{readonly: true}),
            FormNumberComponent.makeCurrency( 'VAT Amount', 'vatAmount', {readonly: true}),
            FormNumberComponent.makeCurrency( 'Total', 'total', {readonly: true, calculateValue: (o:VatReturnTxn) => o.netAmount + o.vatAmount}),
        ];          
    }

}
